import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000]; // Exponential backoff

export function useRealtimeNotifications() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileIdRef = useRef<string | undefined>(profile?.id);

  // Keep profileIdRef updated
  useEffect(() => {
    profileIdRef.current = profile?.id;
  }, [profile?.id]);

  const handleClubJoinRequestInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    const { data: membership } = await supabase
      .from('club_members')
      .select('role, club:clubs(name)')
      .eq('club_id', payload.new.club_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin') {
      const clubName = (membership.club as any)?.name || 'CLB';
      toast.info('Đơn xin gia nhập mới', {
        description: `Có người muốn tham gia ${clubName}`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/clubs/${payload.new.club_id}/manage`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['join-requests'] });
    }
  }, [queryClient]);

  const handleClubJoinRequestUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    if (payload.new.profile_id === currentProfileId) {
      const { data: club } = await supabase
        .from('clubs')
        .select('name')
        .eq('id', payload.new.club_id)
        .maybeSingle();

      if (payload.new.status === 'approved') {
        toast.success('Đơn được chấp nhận! 🎉', {
          description: `Bạn đã trở thành thành viên của ${club?.name || 'CLB'}`,
        });
        queryClient.invalidateQueries({ queryKey: ['my-clubs'] });
      } else if (payload.new.status === 'rejected') {
        toast.error('Đơn bị từ chối', {
          description: `${club?.name || 'CLB'} đã từ chối đơn xin gia nhập của bạn`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['join-requests'] });
    }
  }, [queryClient]);

  const handleMatchJoinRequestInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    const { data: match } = await supabase
      .from('matches')
      .select('id, sport, created_by')
      .eq('id', payload.new.match_id)
      .maybeSingle();

    if (match?.created_by === currentProfileId) {
      const { data: requester } = await supabase
        .from('profiles_public')
        .select('name')
        .eq('id', payload.new.profile_id)
        .maybeSingle();

      toast.info('Yêu cầu tham gia mới', {
        description: `${requester?.name || 'Người chơi'} muốn tham gia trận đấu của bạn`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/matches/${payload.new.match_id}`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['match-join-requests', payload.new.match_id] });
    }
  }, [queryClient]);

  const handleMatchJoinRequestUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    if (payload.new.profile_id === currentProfileId) {
      if (payload.new.status === 'approved') {
        toast.success('Yêu cầu được chấp nhận! 🎉', {
          description: 'Bạn đã được thêm vào trận đấu',
          action: {
            label: 'Xem trận',
            onClick: () => {
              window.location.href = `/matches/${payload.new.match_id}`;
            },
          },
        });
        queryClient.invalidateQueries({ queryKey: ['my-matches'] });
        queryClient.invalidateQueries({ queryKey: ['match', payload.new.match_id] });
      } else if (payload.new.status === 'rejected') {
        toast.error('Yêu cầu bị từ chối', {
          description: 'Yêu cầu tham gia trận đấu của bạn đã bị từ chối',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['my-match-join-request', payload.new.match_id] });
    }
  }, [queryClient]);

  const handleMatchResultInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId || payload.new.submitted_by === currentProfileId) return;

    const { data: participation } = await supabase
      .from('match_participants')
      .select('match:matches(sport, date)')
      .eq('match_id', payload.new.match_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (participation) {
      toast.info('Kết quả trận đấu mới', {
        description: 'Đối thủ đã gửi kết quả. Hãy xác nhận!',
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/matches/${payload.new.match_id}`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['my-matches'] });
    }
  }, [queryClient]);

  const handleMatchResultUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    const newData = payload.new as any;
    const oldData = payload.old as any;
    
    if (newData?.is_confirmed && !oldData?.is_confirmed) {
      const { data: participation } = await supabase
        .from('match_participants')
        .select('*')
        .eq('match_id', newData.match_id)
        .eq('profile_id', currentProfileId)
        .maybeSingle();

      if (participation) {
        const isWinner = newData.winner_id === currentProfileId || 
          newData.winner_team === participation.team;
        
        if (isWinner) {
          toast.success('Bạn thắng! 🏆', {
            description: 'Kết quả đã được xác nhận. Rank đã cập nhật!',
          });
        } else {
          toast.info('Trận đấu kết thúc', {
            description: 'Kết quả đã được xác nhận. Rank đã cập nhật.',
          });
        }
        
        queryClient.invalidateQueries({ queryKey: ['matches'] });
        queryClient.invalidateQueries({ queryKey: ['my-matches'] });
        queryClient.invalidateQueries({ queryKey: ['players'] });
        queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    }
  }, [queryClient]);

  const handleFollowerInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId || payload.new.following_id !== currentProfileId) return;

    const { data: follower } = await supabase
      .from('profiles_public')
      .select('id, name, avatar')
      .eq('id', payload.new.follower_id)
      .maybeSingle();

    if (follower) {
      // Check if this creates a mutual follow (new friendship)
      const { data: isMutual } = await supabase
        .from('user_follows')
        .select('id')
        .eq('follower_id', currentProfileId)
        .eq('following_id', payload.new.follower_id)
        .maybeSingle();

      if (isMutual) {
        toast.success('Bạn bè mới! 🎉', {
          description: `${follower.name} và bạn giờ đã là bạn bè`,
          action: {
            label: 'Xem',
            onClick: () => {
              window.location.href = `/players/${follower.id}`;
            },
          },
        });
        queryClient.invalidateQueries({ queryKey: ['friends'] });
        queryClient.invalidateQueries({ queryKey: ['friend-profiles'] });
      } else {
        toast.success('Người theo dõi mới! 🎉', {
          description: `${follower.name} đã bắt đầu theo dõi bạn`,
          action: {
            label: 'Xem',
            onClick: () => {
              window.location.href = `/players/${follower.id}`;
            },
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: ['follow-counts', currentProfileId] });
      queryClient.invalidateQueries({ queryKey: ['followers-list', currentProfileId] });
      queryClient.invalidateQueries({ queryKey: ['is-followed-by'] });
    }
  }, [queryClient]);

  const handleClubChallengeInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role, club:clubs(name)')
      .eq('club_id', payload.new.challenged_club_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin') {
      const { data: challengerClub } = await supabase
        .from('clubs')
        .select('name')
        .eq('id', payload.new.challenger_club_id)
        .maybeSingle();

      toast.info('Lời thách đấu mới! ⚔️', {
        description: `${challengerClub?.name || 'CLB'} muốn thách đấu ${(membership.club as any)?.name || 'CLB của bạn'}`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/clubs/${payload.new.challenged_club_id}`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['pending-challenges'] });
      queryClient.invalidateQueries({ queryKey: ['club-match-challenges'] });
    }
  }, [queryClient]);

  const handleClubChallengeUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const newData = payload.new as any;
    const oldData = payload.old as any;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role, club:clubs(name)')
      .eq('club_id', newData.challenger_club_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin' && oldData?.status === 'pending') {
      const { data: challengedClub } = await supabase
        .from('clubs')
        .select('name')
        .eq('id', newData.challenged_club_id)
        .maybeSingle();

      if (newData.status === 'accepted') {
        toast.success('Thách đấu được chấp nhận! 🎉', {
          description: `${challengedClub?.name || 'CLB'} đã chấp nhận thách đấu`,
          action: newData.match_id ? {
            label: 'Xem trận',
            onClick: () => {
              window.location.href = `/matches/${newData.match_id}`;
            },
          } : undefined,
        });
        queryClient.invalidateQueries({ queryKey: ['matches'] });
      } else if (newData.status === 'rejected') {
        toast.error('Thách đấu bị từ chối', {
          description: `${challengedClub?.name || 'CLB'} đã từ chối thách đấu`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['club-match-challenges'] });
      queryClient.invalidateQueries({ queryKey: ['pending-challenges'] });
    }
  }, [queryClient]);

  const handleMatchInvitationUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    
    const newData = payload.new as any;
    const oldData = payload.old as any;
    
    if (newData.inviter_id === currentProfileId && oldData?.status === 'pending') {
      const { data: invitee } = await supabase
        .from('profiles_public')
        .select('name')
        .eq('id', newData.invitee_id)
        .maybeSingle();

      if (newData.status === 'accepted') {
        toast.success('Lời mời được chấp nhận! 🎉', {
          description: `${invitee?.name || 'Người chơi'} đã chấp nhận tham gia trận đấu`,
          action: {
            label: 'Xem trận',
            onClick: () => {
              window.location.href = `/matches/${newData.match_id}`;
            },
          },
        });
      } else if (newData.status === 'rejected') {
        toast.info('Lời mời bị từ chối', {
          description: `${invitee?.name || 'Người chơi'} đã từ chối lời mời tham gia`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['match-invitations', newData.match_id] });
      queryClient.invalidateQueries({ queryKey: ['match', newData.match_id] });
    }
  }, [queryClient]);

  const handleTeamMatchInvitationInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const { data: membership } = await supabase
      .from('team_members')
      .select('role, team:teams(name)')
      .eq('team_id', payload.new.invitee_team_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin') {
      const { data: inviterTeam } = await supabase
        .from('teams')
        .select('name')
        .eq('id', payload.new.inviter_team_id)
        .maybeSingle();

      toast.info('Lời mời tham gia trận đội ⚔️', {
        description: `Đội ${inviterTeam?.name || 'đối thủ'} mời đội ${(membership.team as any)?.name || 'bạn'} tham gia trận đấu`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = '/profile/team-match-invitations';
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['my-team-pending-invitations'] });
    }
  }, [queryClient]);

  const handleTeamMatchInvitationUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const newData = payload.new as any;
    const oldData = payload.old as any;

    const { data: membership } = await supabase
      .from('team_members')
      .select('role, team:teams(name)')
      .eq('team_id', newData.inviter_team_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin' && oldData?.status === 'pending') {
      const { data: inviteeTeam } = await supabase
        .from('teams')
        .select('name')
        .eq('id', newData.invitee_team_id)
        .maybeSingle();

      if (newData.status === 'accepted') {
        toast.success('Đội đã chấp nhận! 🎉', {
          description: `Đội ${inviteeTeam?.name || 'đối thủ'} đã chấp nhận tham gia trận đấu`,
          action: {
            label: 'Xem trận',
            onClick: () => {
              window.location.href = `/matches/${newData.match_id}`;
            },
          },
        });
      } else if (newData.status === 'rejected') {
        toast.info('Lời mời bị từ chối', {
          description: `Đội ${inviteeTeam?.name || 'đối thủ'} đã từ chối tham gia trận đấu`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['match-team-invitations', newData.match_id] });
      queryClient.invalidateQueries({ queryKey: ['match', newData.match_id] });
    }
  }, [queryClient]);

  const handleSpectatorInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const { data: match } = await supabase
      .from('matches')
      .select('id, created_by')
      .eq('id', payload.new.match_id)
      .maybeSingle();

    if (match?.created_by === currentProfileId && payload.new.profile_id !== currentProfileId) {
      const { data: spectator } = await supabase
        .from('profiles_public')
        .select('name')
        .eq('id', payload.new.profile_id)
        .maybeSingle();

      toast.info('Yêu cầu làm khán giả 👀', {
        description: `${spectator?.name || 'Người dùng'} muốn xem trận đấu của bạn`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/matches/${payload.new.match_id}`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['match-spectators', payload.new.match_id] });
    }
  }, [queryClient]);

  const handleSpectatorUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const newData = payload.new as any;
    const oldData = payload.old as any;

    if (newData.profile_id === currentProfileId && oldData?.status === 'pending') {
      if (newData.status === 'approved') {
        toast.success('Được duyệt làm khán giả! 🎉', {
          description: 'Bạn đã được chấp nhận xem trận đấu',
          action: {
            label: 'Xem trận',
            onClick: () => {
              window.location.href = `/matches/${newData.match_id}`;
            },
          },
        });
      } else if (newData.status === 'rejected') {
        toast.info('Yêu cầu bị từ chối', {
          description: 'Yêu cầu xem trận đấu của bạn đã bị từ chối',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['match-spectators', newData.match_id] });
    }
  }, [queryClient]);

  const handleQuickMatchSignupInsert = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    const newRow = payload.new as any;

    // Find the quick match's club and check if current user is admin of that club
    const { data: qm } = await supabase
      .from('club_quick_matches')
      .select('club_id, title, club:clubs(name)')
      .eq('id', newRow.quick_match_id)
      .maybeSingle();
    if (!qm) return;

    const { data: membership } = await supabase
      .from('club_members')
      .select('role')
      .eq('club_id', (qm as any).club_id)
      .eq('profile_id', currentProfileId)
      .maybeSingle();

    if (membership?.role === 'admin') {
      const clubName = (qm as any)?.club?.name || 'CLB';
      toast.info('Đăng ký tuyển nhanh mới ⚡', {
        description: `Có người vừa đăng ký tham gia "${(qm as any).title}" — ${clubName}`,
        action: {
          label: 'Xem',
          onClick: () => {
            window.location.href = `/clubs/${(qm as any).club_id}`;
          },
        },
      });
      queryClient.invalidateQueries({ queryKey: ['club-quick-matches'] });
      queryClient.invalidateQueries({ queryKey: ['quick-match-signups'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  }, [queryClient]);

  const handleQuickMatchSignupUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;
    const newRow = payload.new as any;
    const oldRow = payload.old as any;

    if (newRow.profile_id !== currentProfileId) return;
    if (oldRow?.approval_status === newRow.approval_status) return;

    if (newRow.approval_status === 'approved') {
      toast.success('Yêu cầu tuyển nhanh được duyệt 🎉', {
        description: 'Hãy thanh toán (nếu có phí) để hoàn tất đăng ký.',
      });
    } else if (newRow.approval_status === 'rejected') {
      toast.error('Yêu cầu tuyển nhanh bị từ chối', {
        description: newRow.rejected_reason || 'Admin CLB đã từ chối yêu cầu.',
      });
    }
    queryClient.invalidateQueries({ queryKey: ['club-quick-matches'] });
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  const handleEscrowPaymentUpdate = useCallback(async (payload: RealtimePostgresChangesPayload<any>) => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId) return;

    const newData = payload.new as any;
    const oldData = payload.old as any;
    if (!newData) return;
    const isPayer = newData.payer_id === currentProfileId;
    const isPayee = newData.payee_id === currentProfileId;
    if (!isPayer && !isPayee) return;

    const becamePaid =
      newData.payment_status === 'paid' && oldData?.payment_status !== 'paid';

    if (becamePaid) {
      const activityName = newData.activity_name || 'giao dịch';
      const activityType = newData.activity_type as string | undefined;
      let href: string | undefined;
      if (activityType === 'match' || activityType === 'match_join' || activityType === 'spectator') {
        href = newData.activity_id ? `/matches/${newData.activity_id}` : undefined;
      }

      if (isPayer) {
        toast.success('PayOS đã xác nhận thanh toán ✅', {
          description: `${activityName} đã chuyển sang trạng thái đã thanh toán`,
          action: href
            ? { label: 'Xem', onClick: () => { window.location.href = href!; } }
            : undefined,
        });
      } else if (isPayee) {
        const netAmount = Number(newData.net_amount || 0);
        const formatted = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(netAmount);
        toast.success('Bạn vừa nhận một khoản thanh toán 💰', {
          description: `${formatted} từ ${activityName} đang được giữ trong hệ thống`,
          action: { label: 'Xem', onClick: () => { window.location.href = '/payments?tab=income'; } },
        });
      }

      // Refresh all request/detail/financial caches so UI updates instantly
      queryClient.invalidateQueries({ queryKey: ['my-unified-requests'] });
      queryClient.invalidateQueries({ queryKey: ['request-detail'] });
      queryClient.invalidateQueries({ queryKey: ['escrow-payments'] });
      queryClient.invalidateQueries({ queryKey: ['escrow-payment', newData.id] });
      queryClient.invalidateQueries({ queryKey: ['my-matches'] });
      queryClient.invalidateQueries({ queryKey: ['my-classes'] });
      queryClient.invalidateQueries({ queryKey: ['my-booking-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-activity-payments'] });
      queryClient.invalidateQueries({ queryKey: ['my-incoming-payments'] });
      queryClient.invalidateQueries({ queryKey: ['user-financial-summary'] });
      queryClient.invalidateQueries({ queryKey: ['provider-earnings'] });
      queryClient.invalidateQueries({ queryKey: ['provider-balance'] });
      if (newData.activity_id) {
        queryClient.invalidateQueries({ queryKey: ['match', newData.activity_id] });
      }
    } else if (
      newData.payment_status === 'refunded' &&
      oldData?.payment_status !== 'refunded'
    ) {
      toast.info('Đã hoàn tiền', {
        description: `${newData.activity_name || 'Giao dịch'} đã được hoàn tiền`,
      });
      queryClient.invalidateQueries({ queryKey: ['my-unified-requests'] });
      queryClient.invalidateQueries({ queryKey: ['request-detail'] });
      queryClient.invalidateQueries({ queryKey: ['user-financial-summary'] });
      queryClient.invalidateQueries({ queryKey: ['my-incoming-payments'] });
    }
  }, [queryClient]);

  const setupChannel = useCallback(() => {
    const currentProfileId = profileIdRef.current;
    if (!currentProfileId || channelRef.current) return;

    console.log('Setting up consolidated realtime channel for:', currentProfileId);

    const channel = supabase
      .channel('consolidated-notifications', {
        config: {
          broadcast: { self: true },
        },
      })
      // Club join requests
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'club_join_requests' }, handleClubJoinRequestInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'club_join_requests' }, handleClubJoinRequestUpdate)
      // Match join requests
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'match_join_requests' }, handleMatchJoinRequestInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'match_join_requests' }, handleMatchJoinRequestUpdate)
      // Match invitations
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'match_invitations' }, handleMatchInvitationUpdate)
      // Match results
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'match_results' }, handleMatchResultInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'match_results' }, handleMatchResultUpdate)
      // Followers
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_follows' }, handleFollowerInsert)
      // Club challenges
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'club_match_challenges' }, handleClubChallengeInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'club_match_challenges' }, handleClubChallengeUpdate)
      // Team match invitations
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'match_team_invitations' }, handleTeamMatchInvitationInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'match_team_invitations' }, handleTeamMatchInvitationUpdate)
      // Spectators
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'match_spectators' }, handleSpectatorInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'match_spectators' }, handleSpectatorUpdate)
      // Escrow payments — notify payer when PayOS confirms
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'escrow_payments' }, handleEscrowPaymentUpdate)
      // Quick match signups (Tuyển nhanh)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'club_quick_match_signups' }, handleQuickMatchSignupInsert)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'club_quick_match_signups' }, handleQuickMatchSignupUpdate);

    channel.subscribe((status, err) => {
      console.log('Consolidated notifications channel status:', status);
      
      if (status === 'SUBSCRIBED') {
        reconnectAttemptRef.current = 0; // Reset on successful connection
      } else if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
        console.error('Channel error or closed:', err);
        channelRef.current = null;
        
        // Attempt reconnection with exponential backoff
        const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
        console.log(`Attempting reconnection in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptRef.current++;
          supabase.removeChannel(channel);
          setupChannel();
        }, delay);
      }
    });

    channelRef.current = channel;
  }, [
    handleClubJoinRequestInsert,
    handleClubJoinRequestUpdate,
    handleMatchJoinRequestInsert,
    handleMatchJoinRequestUpdate,
    handleMatchInvitationUpdate,
    handleMatchResultInsert,
    handleMatchResultUpdate,
    handleFollowerInsert,
    handleClubChallengeInsert,
    handleClubChallengeUpdate,
    handleTeamMatchInvitationInsert,
    handleTeamMatchInvitationUpdate,
    handleSpectatorInsert,
    handleSpectatorUpdate,
    handleEscrowPaymentUpdate,
    handleQuickMatchSignupInsert,
    handleQuickMatchSignupUpdate,
  ]);

  useEffect(() => {
    if (profile?.id) {
      setupChannel();
    }

    return () => {
      console.log('Cleaning up consolidated notifications channel');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [profile?.id, setupChannel]);
}
